import React, { FC, useState } from 'react';
import { DELETE_ACCOUNT_CONFIRMATION } from '@dreamai/types';
import './delete-account-modal.scss';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  error?: string | null;
}

const DeleteAccountModal: FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  error,
}) => {
  const [confirmationText, setConfirmationText] = useState('');

  if (!isOpen) return null;

  const confirmed = confirmationText === DELETE_ACCOUNT_CONFIRMATION;

  const handleClose = () => {
    if (isSubmitting) return;
    setConfirmationText('');
    onClose();
  };

  return (
    <div
      className='delete-account-modal-overlay'
      onClick={handleClose}
      data-testid='delete-account-modal'
    >
      <div
        className='delete-account-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='delete-account-modal-title'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-header'>
          <p className='modal-title' id='delete-account-modal-title'>
            Delete your account
          </p>
          <button
            className='close-button'
            onClick={handleClose}
            aria-label='Close'
            disabled={isSubmitting}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#374562'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>

        <div className='modal-body'>
          <h3 className='body-title'>This cannot be undone</h3>
          <div className='body-text'>
            <p>
              Deleting your account permanently and irrevocably removes all the
              data Dream holds about you: your secretary, conversations,
              memories and facts, reminders and projects, uploaded files,
              connected-app access tokens, and your billing history. Your login
              will be deleted as well.
            </p>
            <p>
              If you are the last member of your team, any active subscription
              is cancelled immediately, unused card balance is refunded to your
              card, and saved cards are removed.
            </p>
            <p>
              There is no recovery period and no way to restore any of this
              afterwards.
            </p>
          </div>

          <div className='confirm-field'>
            <label className='confirm-label' htmlFor='delete-account-confirm'>
              Type <strong>{DELETE_ACCOUNT_CONFIRMATION}</strong> to confirm
            </label>
            <input
              id='delete-account-confirm'
              className='confirm-input'
              type='text'
              autoComplete='off'
              spellCheck={false}
              value={confirmationText}
              disabled={isSubmitting}
              onChange={(e) => setConfirmationText(e.target.value)}
            />
          </div>
        </div>

        <div className='modal-footer'>
          {error && <p className='delete-error'>{error}</p>}
          <button
            className='btn-delete-dismiss'
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Keep my account
          </button>
          <button
            className='btn-delete-confirm'
            onClick={onConfirm}
            disabled={!confirmed || isSubmitting}
          >
            {isSubmitting ? 'Deleting...' : 'Permanently delete my account'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
